import React from 'react';

interface TimerControlsProps {
  isRunning: boolean;
  isCompleted: boolean;
  onToggle: () => void;
  onReset: () => void;
  onNext?: () => void;
  onSkip?: () => void;
}

const TimerControls: React.FC<TimerControlsProps> = ({ 
  isRunning, 
  isCompleted,
  onToggle, 
  onReset,
  onNext,
  onSkip 
}) => {
  return (
    <div className="flex flex-col items-center space-y-6">
      {/* Main control button */}
      <button
        onClick={onToggle}
        disabled={isCompleted}
        className={`
          w-20 h-20 rounded-full flex items-center justify-center
          transition-all duration-300 shadow-lg hover:shadow-xl
          focus:outline-none focus:ring-4 focus:ring-opacity-50
          ${isRunning
            ? 'bg-alert hover:bg-alert/90 focus:ring-alert/30 text-primary-text'
            : 'bg-primary hover:bg-accent focus:ring-primary/30 text-primary-text'
          }
          ${isCompleted 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:scale-105 active:scale-95'
          }
        `}
        aria-label={isRunning ? 'Pause timer' : 'Start timer'}
      >
        {isRunning ? (
          // Pause icon
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
          </svg>
        ) : (
          // Play icon
          <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        )}
      </button>

      {/* Secondary controls */}
      <div className="flex space-x-4">
        {/* Reset button */}
        <button
          onClick={onReset}
          className="
            px-6 py-3 bg-surface hover:bg-accent/30 rounded-lg
            text-text font-medium transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-primary/30
            hover:scale-105 active:scale-95
          "
          aria-label="Reset timer"
        >
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Reset</span>
          </div>
        </button>

        {/* Skip session button (shown when running) */}
        {!isCompleted && onSkip && (
          <button
            onClick={onSkip}
            className="
              px-6 py-3 bg-alert hover:bg-alert/90 rounded-lg
              text-primary-text font-medium transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-alert/30
              hover:scale-105 active:scale-95
            "
            aria-label="Skip current session"
          >
            <div className="flex items-center space-x-2">
              <span>Skip</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        )}

        {/* Next session button (shown when completed) */}
        {isCompleted && onNext && (
          <button
            onClick={onNext}
            className="
              px-6 py-3 bg-primary hover:bg-accent rounded-lg
              text-primary-text font-medium transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-primary/30
              hover:scale-105 active:scale-95 animate-pulse
            "
            aria-label="Start next session"
          >
            <div className="flex items-center space-x-2">
              <span>Next Session</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        )}
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="text-sm text-text/60 text-center">
        <p>Press <kbd className="px-2 py-1 bg-surface rounded text-xs">Space</kbd> to play/pause</p>
      </div>
    </div>
  );
};

export default TimerControls; 